import { createScoreEngine } from '@velainc/listing-score-engine';
import { Map } from 'immutable';
import Big from 'big.js';

import { convertBodyHTMLToDescription } from '@/utils/product/convert';
import { shapeKeywordsForAPI } from '@/utils/product/shapeAttributes';
import { tagsToArray } from '@/utils/tags';
import { getSize } from '@/utils/iterable/getSize';
import { keepOld } from '@/utils/iterable/keepOld';

import { EBAY, ETSY, FAIRE, SHOPIFY } from '@/constants/channels';
import { GRADES, NEW, VALUE } from '@/constants/product';
import { ETSY_AI_CHANNELS } from '@/constants/bulkEdit';
import { ATTRIBUTE } from '@/constants/attributes';
import { FEATURE } from '@/constants/features';
import { EMPTY } from '@/constants';
import { FORM } from '@/constants/forms';

const scoreEngine = createScoreEngine();

function shapeScoresForApp({ channel, scores = {}, shop, userFeatures }) {
  function shapeDetails(components = []) {
    function reduceComponents(result, component) {
      const name = Object.keys(component)[0];
      const value = component[name] === 'complete';
      return result.push(Map({ name, value }));
    }

    return components.reduce(reduceComponents, EMPTY.LIST);
  }

  switch (channel) {
    case EBAY: {
      const {
        descriptionScore,
        descriptionScoreComponents = {},
        overallScore,
        photosScore,
        photosScoreComponents = {},
        titleScore,
        titleScoreComponents = {},
      } = scores;

      let result = Map({
        total: overallScore,
        [ATTRIBUTE.TITLE]: Map({
          details: shapeDetails(titleScoreComponents.breakdown),
          total: titleScore,
        }),
        [ATTRIBUTE.DESCRIPTION]: Map({
          details: shapeDetails(descriptionScoreComponents.breakdown),
          total: descriptionScore,
        }),
      });

      if (
        !shop.get('isVelaOnlyShop') &&
        userFeatures.get(FEATURE.PHOTO_SCORING) &&
        userFeatures.get(FEATURE.AI_CREDITS)
      ) {
        result = result.set(ATTRIBUTE.PHOTOS,
          Map({
            details: shapeDetails(photosScoreComponents.breakdown),
            total: photosScore,
          })
        );
      }

      return result;
    }

    case ETSY: {
      const {
        descriptionScore,
        descriptionScoreComponents = {},
        overallScore,
        photosScore,
        photosScoreComponents = {},
        tagsScore,
        tagsScoreComponents = {},
        titleScore,
        titleScoreComponents = {},
      } = scores;

      let result = Map({
        total: overallScore,
        [ATTRIBUTE.TITLE]: Map({
          details: shapeDetails(titleScoreComponents.breakdown),
          total: titleScore,
        }),
        [ATTRIBUTE.DESCRIPTION]: Map({
          details: shapeDetails(descriptionScoreComponents.breakdown),
          total: descriptionScore,
        }),
        [ATTRIBUTE.TAGS]: Map({
          details: shapeDetails(tagsScoreComponents.breakdown),
          total: tagsScore,
        }),
      });

      if (!shop.get('isVelaOnlyShop') && userFeatures.get(FEATURE.PHOTO_SCORING)) {
        result = result.set(ATTRIBUTE.PHOTOS,
          Map({
            details: shapeDetails(photosScoreComponents.breakdown),
            total: photosScore,
          })
        );
      }

      return result;
    }

    case FAIRE: {
      const {
        descriptionScore,
        descriptionScoreComponents = {},
        overallScore,
        photosScore,
        photosScoreComponents = {},
        titleScore,
        titleScoreComponents = {},
      } = scores;

      let result = Map({
        total: overallScore,
        [ATTRIBUTE.TITLE]: Map({
          details: shapeDetails(titleScoreComponents.breakdown),
          total: titleScore,
        }),
        [ATTRIBUTE.DESCRIPTION]: Map({
          details: shapeDetails(descriptionScoreComponents.breakdown),
          total: descriptionScore,
        }),
      });

      if (
        !shop.get('isVelaOnlyShop') &&
        userFeatures.get(FEATURE.PHOTO_SCORING) &&
        userFeatures.get(FEATURE.AI_CREDITS)
      ) {
        result = result.set(ATTRIBUTE.PHOTOS,
          Map({
            details: shapeDetails(photosScoreComponents.breakdown),
            total: photosScore,
          })
        );
      }

      return result;
    }

    case SHOPIFY: {
      const {
        descriptionScore,
        descriptionScoreComponents = {},
        descriptionSEOScore,
        descriptionSEOScoreComponents = {},
        overallScore,
        photosScore,
        photosScoreComponents = {},
        titleScore,
        titleScoreComponents = {},
        titleSEOScore,
        titleSEOScoreComponents = {},
      } = scores;

      let result = Map({
        total: overallScore,
        [ATTRIBUTE.TITLE]: Map({
          details: shapeDetails(titleScoreComponents.breakdown),
          total: titleScore,
        }),
        [ATTRIBUTE.DESCRIPTION]: Map({
          details: shapeDetails(descriptionScoreComponents.breakdown),
          total: descriptionScore,
        }),
        [ATTRIBUTE.PAGE_TITLE]: Map({
          details: shapeDetails(titleSEOScoreComponents.breakdown),
          total: titleSEOScore,
        }),
        [ATTRIBUTE.META_DESCRIPTION]: Map({
          details: shapeDetails(descriptionSEOScoreComponents.breakdown),
          total: descriptionSEOScore,
        }),
      });

      if (!shop.get('isVelaOnlyShop') && userFeatures.get(FEATURE.PHOTO_SCORING)) {
        result = result.set(ATTRIBUTE.PHOTOS,
          Map({
            details: shapeDetails(photosScoreComponents.breakdown),
            total: photosScore,
          })
        );
      }

      return result;
    }

    default: {
      return EMPTY.MAP;
    }
  }
}

export function updateListingScore({ product, shop, userFeatures }) {
  const channel = product.get('channel');

  function reducePhotos(result, photo) {
    result.push({
      alt_text: photo.get('alt_text'),
      height: photo.get('height'),
      isWhite: photo.get('white_background') ?? null,
      order_id: result.length + 1,
      width: photo.get('width'),
      url: photo.get('fullsize_url'),
    });

    return result;
  }

  switch (channel) {
    case EBAY: {
      const descriptionHTML = product.getIn([FORM.DESCRIPTION, VALUE], '');
      const descriptionText = convertBodyHTMLToDescription(descriptionHTML);
      const listing = {
        channel: EBAY,
        title: product.getIn([FORM.TITLE, VALUE]),
        description: descriptionText,
      };

      const {
        all_keywords: allKeywords,
        focus_keyword: focusKeyword,
      } = shapeKeywordsForAPI({ value: product.get(ATTRIBUTE.KEYWORDS) });

      listing.all_keywords = allKeywords;
      listing.focus_keyword = focusKeyword;

      if (!shop.get('isVelaOnlyShop') && userFeatures.get(FEATURE.PHOTO_SCORING) && userFeatures.get(FEATURE.AI_CREDITS)) {
        listing.photos = product.getIn([FORM.PHOTOS, VALUE]).reduce(reducePhotos, []);
      }

      const scores = scoreEngine.scoreListing(listing, { includeComponents: true });

      return product.set('score', shapeScoresForApp({ channel, scores, shop, userFeatures }));
    }

    case ETSY: {
      const isVelaOnly = shop.get('isVelaOnlyShop');
      const titleFormatPreference = shop.get('titleFormatPreference');
      const scoreChannel = ETSY_AI_CHANNELS[titleFormatPreference] || ETSY;
      const listing = {
        channel: scoreChannel || ETSY,
        description: product.getIn([FORM.DESCRIPTION, VALUE]),
        title: product.getIn([FORM.TITLE, VALUE]),
        tags: tagsToArray(
          product
            .getIn([FORM.TAGS, VALUE], EMPTY.ORDERED_MAP)
            .mergeWith(keepOld, product.getIn([FORM.TAGS, NEW]))
        ),
      };

      if (!isVelaOnly && userFeatures.get(FEATURE.PHOTO_SCORING)) {
        listing.photos = product.getIn([FORM.PHOTOS, VALUE]).reduce(reducePhotos, []);
      }

      const scores = scoreEngine.scoreListing(listing, { includeComponents: true });

      return product.set('score', shapeScoresForApp({ channel, scores, shop, userFeatures }));
    }

    case FAIRE: {
      const isVelaOnly = shop.get('isVelaOnlyShop');
      const listing = {
        channel: FAIRE,
        title: product.getIn([FORM.TITLE, VALUE]),
        description: product.getIn([FORM.DESCRIPTION, VALUE], ''),
      };

      if (!isVelaOnly && userFeatures.get(FEATURE.PHOTO_SCORING) && userFeatures.get(FEATURE.AI_CREDITS)) {
        listing.photos = product.getIn([FORM.PHOTOS, VALUE]).reduce(reducePhotos, []);
      }

      const scores = scoreEngine.scoreListing(listing, { includeComponents: true });

      return product.set('score', shapeScoresForApp({ channel, scores, shop, userFeatures }));
    }

    case SHOPIFY: {
      const isVelaOnly = shop.get('isVelaOnlyShop');
      const listing = {
        channel: SHOPIFY,
        title: product.getIn([FORM.TITLE, VALUE]),
      };

      const {
        all_keywords: allKeywords,
        focus_keyword: focusKeyword,
      } = shapeKeywordsForAPI({ channel, value: product.get(ATTRIBUTE.KEYWORDS) });

      listing.all_keywords = allKeywords;
      listing.description = product.getIn([FORM.DESCRIPTION, VALUE], '');
      listing.focus_keyword = focusKeyword;
      listing.seo_description = product.getIn([FORM.SEO, ATTRIBUTE.META_DESCRIPTION, VALUE], '');
      listing.seo_title = product.getIn([FORM.SEO, ATTRIBUTE.PAGE_TITLE, VALUE], '');

      if (!isVelaOnly && userFeatures.get(FEATURE.PHOTO_SCORING)) {
        listing.photos = product.getIn([FORM.PHOTOS, VALUE]).reduce(reducePhotos, []);
      }

      const scores = scoreEngine.scoreListing(listing, { includeComponents: true });

      return product.set('score', shapeScoresForApp({ channel, scores, shop, userFeatures }));
    }

    default: {
      return product;
    }
  }
}

export function getScoreGrade({ channel, score, userFeatures }) {
  if (!Number.isFinite(score)) {
    return {
      grade: null,
    };
  }

  const photoScoringEnabled = (channel === EBAY || channel === FAIRE)
    ? userFeatures.get(FEATURE.PHOTO_SCORING) && userFeatures.get(FEATURE.AI_CREDITS)
    : userFeatures.get(FEATURE.PHOTO_SCORING);

  const scoreGradeIndex = photoScoringEnabled
    ? new Big(Math.max(0, Math.min(4, score))).div(0.27).round(0, 0)
    : new Big(Math.max(0, Math.min(4, score))).minus(1).div(0.2).round(0, 0);

  return {
    grade: GRADES[Math.max(0, Math.min(getSize(GRADES) - 1, scoreGradeIndex.toNumber()))],
    normalized: Math.max(0, Math.min(1, scoreGradeIndex.div(getSize(GRADES) - 1).toNumber())),
  };
}
